// src/contexts/ArtistAuthContext.js
import React, { createContext, useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { SessionUnavailableBanner, useAuth } from './AuthContext';
import { startPresenceHeartbeat, stopPresenceHeartbeat } from '../lib/presence';
import { getArtistSessionResult } from '../api/auth';
import {
  getArtistDemoIdentity,
  getArtistDemoState,
  subscribeArtistDemo,
} from '../utils/artistDemoRuntime';

const ArtistAuthContext = createContext();
const TRANSIENT_SESSION_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

const isTransientSessionStatus = (status) =>
  TRANSIENT_SESSION_STATUSES.has(Number(status)) || Number(status) >= 500;

export const ArtistAuthProvider = ({ children }) => {
  const [artistUser, setArtistUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [sessionUnavailable, setSessionUnavailable] = useState(false);
  const artistUserRef = useRef(null);
  const { user: adminUser, checking: adminChecking } = useAuth();
  const artistDemo = useSyncExternalStore(subscribeArtistDemo, getArtistDemoState, getArtistDemoState);

  const hasAdminSession = Boolean(adminUser?.isLoggedIn && adminUser?.userType === 'admin');

  useEffect(() => {
    artistUserRef.current = artistUser;
  }, [artistUser]);

  const fetchArtistSession = useCallback(async () => {
    if (artistDemo.active) {
      const demoUser = getArtistDemoIdentity();
      setArtistUser(demoUser);
      setSessionUnavailable(false);
      return demoUser;
    }
    if (hasAdminSession) {
      setArtistUser(null);
      setSessionUnavailable(false);
      return null;
    }
    try {
      const result = await getArtistSessionResult();
      if (!result.ok) {
        if (isTransientSessionStatus(result.status)) {
          setSessionUnavailable(true);
          if (artistUserRef.current) {
            return artistUserRef.current;
          }
          setArtistUser(null);
          return null;
        }
        setArtistUser(null);
        setSessionUnavailable(false);
        return null;
      }
      const data = result.data;
      if (data?.userType !== 'artist') {
        setArtistUser(null);
        setSessionUnavailable(false);
        return null;
      }
      setArtistUser(data);
      setSessionUnavailable(false);
      return data;
    } catch (error) {
      console.warn('Error checking artist session (preserving current page when possible):', error?.message || error);
      setSessionUnavailable(true);
      if (artistUserRef.current) {
        return artistUserRef.current;
      }
      setArtistUser(null);
      return null;
    }
  }, [artistDemo.active, hasAdminSession]);

  // Signup / login 直後など「確実に artistUser を同期してから遷移したい」場合に使用する。
  // checking を true にしてガード側を AuthGateScreen へ寄せ、未同期の一瞬で /artist-login へ飛ぶのを防ぐ。
  const syncArtistSession = async ({ retryDelayMs = 150 } = {}) => {
    if (hasAdminSession && !artistDemo.active) {
      setArtistUser(null);
      setSessionUnavailable(false);
      setChecking(false);
      return null;
    }
    setChecking(true);
    try {
      const session = await fetchArtistSession();
      if (session?.isLoggedIn || session?.userType === 'artist') return session;
      await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
      return await fetchArtistSession();
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      if (adminChecking) return;
      if (hasAdminSession && !artistDemo.active) {
        setArtistUser(null);
        setSessionUnavailable(false);
        if (active) setChecking(false);
        return;
      }
      const first = await fetchArtistSession();
      if (!first) {
        await new Promise((resolve) => setTimeout(resolve, 150));
        await fetchArtistSession();
      }
      if (active) setChecking(false);
    })();
    return () => { active = false; };
  }, [adminChecking, artistDemo.active, hasAdminSession, fetchArtistSession]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let refreshTimer = null;
    const refreshIfVisible = () => {
      if (document.visibilityState === 'hidden') return;
      fetchArtistSession();
    };
    refreshTimer = setInterval(refreshIfVisible, 60 * 1000);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchArtistSession();
      }
    };
    const handleFocus = () => {
      refreshIfVisible();
    };
    const handleSessionRefresh = () => {
      fetchArtistSession();
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('casticart:artist-session-refresh', handleSessionRefresh);

    return () => {
      if (refreshTimer) clearInterval(refreshTimer);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('casticart:artist-session-refresh', handleSessionRefresh);
    };
  }, [fetchArtistSession]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleLogout = (event) => {
      const role = event?.detail?.role;
      if (!role || role === 'artist') {
        setArtistUser(null);
        setSessionUnavailable(false);
      }
    };
    window.addEventListener('casticart:logout', handleLogout);
    return () => window.removeEventListener('casticart:logout', handleLogout);
  }, []);

  // ログイン中のみ在席マネージャを稼働（無操作5分/背景でオフライン化）。
  const isArtistAuthenticated = Boolean(artistUser);
  useEffect(() => {
    if (!isArtistAuthenticated || artistDemo.active) return undefined;
    startPresenceHeartbeat();
    return () => stopPresenceHeartbeat();
  }, [artistDemo.active, isArtistAuthenticated]);

  return (
    <ArtistAuthContext.Provider value={{ artistUser, fetchArtistSession, syncArtistSession, checking, sessionUnavailable, isArtistDemo: artistDemo.active, artistDemoScenario: artistDemo.scenarioId }}>
      {sessionUnavailable && artistUser ? <SessionUnavailableBanner /> : null}
      {children}
    </ArtistAuthContext.Provider>
  );
};

export const useArtistAuth = () => useContext(ArtistAuthContext);
